import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// src/plugins/builder/templates/weekly-trends-summary/generator.tsx
// Scheduled weekly summary. UI also has a "Run it now" button.
import meta from './template.json';
import { manifestFor, header, readme, assemble, safeString } from '../_helpers';
import { useState } from 'react';
import { useReviews, useAI, useToast } from '../../../sdk';
import { Button } from '../../../../ui/Button';
const RuntimeUi = (ctx) => {
    const cfg = ctx.config;
    return _jsx(WeeklyUi, { name: ctx.instanceName, cfg: cfg });
};
function WeeklyUi({ name, cfg }) {
    const { reviews } = useReviews({ limit: 200 });
    const generate = useAI();
    const toast = useToast();
    const [busy, setBusy] = useState(false);
    const [last, setLast] = useState(null);
    async function runNow() {
        setBusy(true);
        try {
            const since = new Date(Date.now() - 7 * 86_400_000).toISOString();
            const recent = (reviews ?? []).filter(r => (r.posted_at ?? '') >= since);
            const avg = recent.length ? (recent.reduce((s, r) => s + (r.rating ?? 0), 0) / recent.length).toFixed(2) : '—';
            const best = cfg.highlightFiveStar ? recent.find(r => (r.rating ?? 0) === 5)?.body : null;
            const ai = await generate({
                messages: [{ role: 'user', content: `Summarize this past week of reviews in 2 friendly sentences. ${recent.length} reviews. Average ${avg}.${best ? ` Best review snippet: "${best.slice(0, 200)}"` : ''}` }],
                prompt_id: 'plugin:builder:weekly-trends',
                max_tokens: 200,
            });
            setLast(ai.text);
            await fetch(cfg.webhookUrl, {
                method: 'POST', headers: { 'content-type': 'application/json' },
                body: JSON.stringify({ summary: ai.text, count: recent.length, average: avg, since, source: 'plugin:builder:weekly-trends-summary' }),
            }).catch(() => { });
            toast.success('Summary sent.');
        }
        catch (e) {
            toast.error(e instanceof Error ? e.message : 'Could not run summary.');
        }
        finally {
            setBusy(false);
        }
    }
    return (_jsxs("main", { id: "main", className: "mx-auto max-w-3xl px-6 py-10", children: [_jsx("h1", { className: "text-2xl font-semibold", children: name }), _jsxs("p", { className: "mt-1 text-sm text-slate-500", children: ["Runs every ", cfg.day, ", sends to your webhook."] }), _jsx("div", { className: "mt-6", children: _jsx(Button, { disabled: busy, onClick: runNow, children: busy ? 'Working…' : 'Run it now' }) }), last && _jsx("p", { className: "mt-6 rounded-2xl bg-white p-4 text-sm ring-1 ring-slate-200 whitespace-pre-wrap", children: last })] }));
}
const runtimeHooks = {
    async onSchedule(ctx) {
        const cfg = ctx.config;
        if (!cfg.webhookUrl)
            return;
        if (!ctx.permissions.includes('external:fetch'))
            return;
        // The host is responsible for filtering by day-of-week before invoking this.
        await fetch(cfg.webhookUrl, {
            method: 'POST', headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ source: 'plugin:builder:weekly-trends-summary', sent_at: new Date().toISOString() }),
        }).catch(() => { });
    },
};
const generate = (params) => {
    const cfg = params.config;
    const manifest = manifestFor({
        instanceId: params.instanceId, name: params.instanceName,
        description: `Weekly AI summary of your reviews, sent to your webhook every ${cfg.day}.`,
        category: 'reporting', icon: 'TrendingUp',
        permissions: ['read:reviews', 'call:ai', 'external:fetch', 'ui:toast'],
        entry: 'both', hooks: { onSchedule: 'weekly' },
        config: meta.configSchema,
        defaultConfig: { webhookUrl: cfg.webhookUrl, day: cfg.day, highlightFiveStar: cfg.highlightFiveStar },
    });
    const pluginTsx = header({ name: params.instanceName, templateId: meta.id, instanceId: params.instanceId }) +
        `import { useReviews, useAI, useToast } from '../sdk';
import { Button } from '../../ui/Button';
import { useState } from 'react';

export default function ${cls(params.instanceId)}() {
  const { reviews } = useReviews({ limit: 200 });
  const generate = useAI();
  const toast = useToast();
  const [last, setLast] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function runNow() {
    setBusy(true);
    try {
      const since = new Date(Date.now() - 7*86400000).toISOString();
      const recent = (reviews ?? []).filter(r => (r.posted_at ?? '') >= since);
      const ai = await generate({
        messages: [{ role: 'user', content: \`Summarize \${recent.length} reviews in 2 sentences.\` }],
        prompt_id: 'plugin:builder:${meta.id}',
        max_tokens: 200,
      });
      setLast(ai.text);
      await fetch('${esc(cfg.webhookUrl)}', { method: 'POST', headers: {'content-type':'application/json'}, body: JSON.stringify({ summary: ai.text, count: recent.length }) }).catch(() => {});
      toast.success('Summary sent.');
    } finally { setBusy(false); }
  }
  return (
    <main id="main" className="mx-auto max-w-3xl px-6 py-10">
      <h1 className="text-2xl font-semibold">${esc(params.instanceName)}</h1>
      <p className="mt-1 text-sm text-slate-500">Runs every ${esc(cfg.day)}.</p>
      <div className="mt-6"><Button disabled={busy} onClick={runNow}>{busy ? 'Working…' : 'Run it now'}</Button></div>
      {last && <p className="mt-6 rounded-2xl bg-white p-4 text-sm ring-1 ring-slate-200 whitespace-pre-wrap">{last}</p>}
    </main>
  );
}
`;
    const hooksTs = header({ name: params.instanceName, templateId: meta.id, instanceId: params.instanceId }) +
        `import type { PluginHandlers, HandlerContext } from '../types';
import { aiGenerate } from '../../ai/client';
import { supabase } from '../../supabase';
export const handlers: PluginHandlers = {
  async onSchedule(ctx: HandlerContext) {
    if (!ctx.permissions.includes('external:fetch')) return;
    if (!ctx.orgId) return;
    const since = new Date(Date.now() - 7*86400000).toISOString();
    const rv = await supabase.from('reviews').select('*').eq('org_id', ctx.orgId).gte('posted_at', since).limit(500);
    const rows = rv.data ?? [];
    let summary = '';
    if (ctx.permissions.includes('call:ai')) {
      try {
        const out = await aiGenerate({ messages: [{ role: 'user', content: \`Summarize \${rows.length} reviews in 2 sentences.\` }], prompt_id: 'plugin:builder:${meta.id}', max_tokens: 200 });
        summary = out.text;
      } catch {}
    }
    await fetch('${esc(cfg.webhookUrl)}', { method: 'POST', headers: {'content-type':'application/json'}, body: JSON.stringify({ summary, count: rows.length, source: 'plugin:builder:${meta.id}' }) }).catch(() => {});
  },
};
export default handlers;
`;
    return assemble({ manifest, pluginTsx, hooksTs,
        readmeMd: readme({ name: params.instanceName, templateId: meta.id,
            description: `Weekly summary of your review trends, posted every ${cfg.day}.`,
            configSummary: [['webhookUrl', cfg.webhookUrl], ['day', cfg.day], ['highlightFiveStar', String(cfg.highlightFiveStar)]],
        }),
    });
};
function esc(s) { return safeString(String(s)); }
function cls(id) { return 'P_' + id.replace(/[^a-zA-Z0-9]+/g, '_'); }
const template = {
    id: meta.id, name: meta.name, tagline: meta.tagline, icon: meta.icon,
    category: meta.category,
    permissions: meta.permissions,
    permissionSummary: meta.permissionSummary,
    entry: meta.entry,
    hooks: meta.hooks,
    configSchema: meta.configSchema,
    defaultConfig: meta.defaultConfig,
    suggestName: (cfg) => `Weekly summary every ${cfg.day || 'Monday'}`,
    describe: (cfg) => `Every ${cfg.day}, summarize your week of reviews and send the summary to your webhook.`,
    generate, runtimeUi: RuntimeUi, runtimeHooks,
    clickCount: meta.clickCount,
};
export default template;
